import { Container, Typography } from '@cleartrip/ct-design-components'
import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { InfoIcon } from '@/assets/icons/Info'
import EarnedLoyaltyPromoBanner from '@/components/EarnedLoyaltyPromoBanner'

type TooltipPlacement = 'left' | 'right'

interface CardBenefitsInfoProps {
    message?: ReactNode
    width?: string
    position?: TooltipPlacement | string
    /** When set (active Elite amend), show promo banner at the bottom of the tooltip */
    changeDateFee?: number | null
}

const DEFAULT_WIDTH = '288px'
const ANCHOR_GAP_PX = 10
const VIEWPORT_PAD_PX = 8
/** Above the fare modal (z 1300) and the design-system tooltip layer. */
const TOOLTIP_Z_INDEX = 100_002

type Coords = { top: number; left: number }

/**
 * Fare-benefit info tooltip.
 *
 * Rendered through a `document.body` portal with fixed positioning instead of the
 * design system's `TooltipV2`: that component absolutely positions its bubble
 * inside the anchor, so every horizontally scrolling fare strip (`ox-scroll`) and
 * every premium fare card that clips its gradient border cut the bubble off.
 */
const CardBenefitsInfo: React.FC<CardBenefitsInfoProps> = ({
    message = '',
    width = '140px',
    position = 'right',
    changeDateFee = null,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [coords, setCoords] = useState<Coords | null>(null)
    const anchorRef = useRef<HTMLDivElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)

    const resolvedWidth = width && width.trim() !== '' ? width : DEFAULT_WIDTH
    const preferLeft = position === 'left'
    const showEliteBanner = changeDateFee != null

    const close = useCallback(() => {
        setIsOpen(false)
        setCoords(null)
    }, [])

    useLayoutEffect(() => {
        if (!isOpen) return
        const anchor = anchorRef.current
        const tooltip = tooltipRef.current
        if (!anchor || !tooltip) return

        const anchorRect = anchor.getBoundingClientRect()
        const { width: tooltipWidth, height: tooltipHeight } = tooltip.getBoundingClientRect()
        const maxLeft = window.innerWidth - tooltipWidth - VIEWPORT_PAD_PX
        const rightSideLeft = anchorRect.right + ANCHOR_GAP_PX
        const leftSideLeft = anchorRect.left - tooltipWidth - ANCHOR_GAP_PX

        // Honour the requested side, but flip when it would leave the viewport.
        let left = preferLeft ? leftSideLeft : rightSideLeft
        if (!preferLeft && left > maxLeft) left = leftSideLeft
        if (preferLeft && left < VIEWPORT_PAD_PX) left = rightSideLeft
        left = Math.max(VIEWPORT_PAD_PX, Math.min(left, Math.max(VIEWPORT_PAD_PX, maxLeft)))

        const centredTop = anchorRect.top + anchorRect.height / 2 - tooltipHeight / 2
        const maxTop = window.innerHeight - tooltipHeight - VIEWPORT_PAD_PX
        const top = Math.max(VIEWPORT_PAD_PX, Math.min(centredTop, Math.max(VIEWPORT_PAD_PX, maxTop)))

        setCoords({ top, left })
    }, [isOpen, preferLeft, message, resolvedWidth, showEliteBanner, changeDateFee])

    // A fixed-position bubble cannot follow its anchor, so dismiss it instead of
    // letting it drift while the fare strip or the modal body scrolls.
    useEffect(() => {
        if (!isOpen) return
        window.addEventListener('scroll', close, true)
        window.addEventListener('resize', close)
        return () => {
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('resize', close)
        }
    }, [isOpen, close])

    const tooltip =
        isOpen && typeof document !== 'undefined'
            ? createPortal(
                  <div
                      ref={tooltipRef}
                      role="tooltip"
                      style={{
                          position: 'fixed',
                          top: coords?.top ?? 0,
                          left: coords?.left ?? 0,
                          width: resolvedWidth,
                          maxWidth: '320px',
                          zIndex: TOOLTIP_Z_INDEX,
                          background: '#1A1A1A',
                          borderRadius: '12px',
                          boxShadow: '0px 4px 16px 0px rgba(26, 26, 26, 0.24)',
                          visibility: coords ? 'visible' : 'hidden',
                          pointerEvents: 'none',
                      }}
                  >
                      <Container className="p-2 br-12">
                          {typeof message === 'string' ? (
                              <Typography color="neutral" variant="L3">
                                  {message}
                              </Typography>
                          ) : (
                              message
                          )}
                          {showEliteBanner && (
                              <Container className="mt-3" data-testid="earned_loyalty_promo_banner">
                                  <EarnedLoyaltyPromoBanner changeDateFee={changeDateFee} />
                              </Container>
                          )}
                      </Container>
                  </div>,
                  document.body
              )
            : null

    return (
        <Container className="ml-1">
            <div
                ref={anchorRef}
                style={{ height: '16px', display: 'inline-flex', cursor: 'pointer' }}
                tabIndex={0}
                onBlur={close}
                onFocus={() => setIsOpen(true)}
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={close}
            >
                <InfoIcon fill="#1A1A1A" />
            </div>
            {tooltip}
        </Container>
    )
}

export default CardBenefitsInfo
